import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import MultiFeatureAnalysisTable, {
  FeatureStatsRowData,
} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisTable';
import tableCs from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisTable.module.scss';
import {ProjectFilter, TagFilter} from 'app/components/Dashboard/utils/chartUtils';
import {getSummaryText} from 'app/components/Dashboard/utils/tableUtils';
import {type ApiOrganization, TagSetting, getAreaUnit} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {MultifeatureAnalysisConfig} from 'app/src/components/Dashboard/types';
import * as C from 'app/utils/constants';
import {ImageLayerInfo} from 'app/utils/layers';
import {getLayer} from 'app/utils/layerUtils';

export interface MultiFeatureAnalysisCategoricalTableProps {
  rows: FeatureStatsRowData[];
  widgetConfig: MultifeatureAnalysisConfig;
  displayFeatureIds: Set<string>;
  setDisplayFeatureIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setHoveredFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
  allDates: string[];
  currentCategoryId: string;
  setCurrentCategoryId: React.Dispatch<React.SetStateAction<string>>;
  dataViewUnit: 'area' | 'percentage';
  setDataViewUnit: React.Dispatch<React.SetStateAction<'area' | 'percentage'>>;
  organization?: I.ImmutableOf<ApiOrganization>;
  hasManyFeatures: boolean;
  categories: string[];
  isExpanded?: boolean;
  filterStates: {
    tagIdsDict: Record<string, TagSetting>;
    projectIdsDict: Record<string, ApiProject>;
    tagFilter: TagFilter;
    projectFilter: ProjectFilter;
  };
  filterActions: {
    applyTagFilter: (tagIDs: Array<string>, tagFilterMode?: 'any' | 'all' | 'none') => void;
    applyProjectFilter: (projectIDs: Array<string>, projectFilterMode?: 'any' | 'none') => void;
  };
}

export const MultiFeatureAnalysisCategoricalTable: React.FC<
  MultiFeatureAnalysisCategoricalTableProps
> = ({
  rows,
  widgetConfig,
  displayFeatureIds,
  setDisplayFeatureIds,
  setHoveredFeatureId,
  allDates,
  currentCategoryId,
  setCurrentCategoryId,
  dataViewUnit,
  setDataViewUnit,
  organization,
  hasManyFeatures,
  categories,
  isExpanded,
  filterStates,
  filterActions,
}) => {
  const layer = getLayer(widgetConfig.layerKey) as ImageLayerInfo;

  const selectItems: {label: string; value: string}[] = React.useMemo(() => {
    return categories.map((key) => ({
      label: layer?.layerLegendMap?.[key]?.label || key,
      value: key,
    }));
  }, [layer, categories]);
  const rowInteractionMode = (() => {
    if (hasManyFeatures && currentCategoryId !== 'multiCategory') {
      return displayFeatureIds.size > 0 ? 'color' : null;
    }
    return 'color';
  })();

  return (
    <MultiFeatureAnalysisTable
      filterStates={filterStates}
      filterActions={filterActions}
      displayFeatureIds={displayFeatureIds}
      setDisplayFeatureIds={setDisplayFeatureIds}
      rows={rows}
      allDates={allDates}
      showAggregateTrend={false}
      widgetConfig={widgetConfig}
      setHoveredFeatureId={setHoveredFeatureId}
      trendLine={{}}
      allowAverage={false}
      rowInteractionMode={rowInteractionMode}
      nameColumnOverride={currentCategoryId === 'multiCategory' ? 'Category' : 'Property'}
      organization={organization}
      isExpanded={isExpanded}
      additionalInputs={
        <>
          <BreakdownToggleAndSelector
            selectItems={selectItems}
            currentCategoryId={currentCategoryId}
            setCurrentCategoryId={setCurrentCategoryId}
            postSelectCategory={() => setDisplayFeatureIds(new Set())}
            layer={layer}
          />
          <B.ButtonGroup minimal outlined className={tableCs.percentageAreaToggle} fill>
            <B.Button
              icon={<B.Icon icon="widget" className={tableCs.icon} />}
              active={dataViewUnit === 'area'}
              onClick={() => setDataViewUnit('area')}
              small
              title={`Show area in ${organization && getAreaUnit(organization) === C.UNIT_AREA_ACRE ? 'acres' : 'hectares'}`}
            />
            <B.Button
              icon={<B.Icon icon="percentage" className={tableCs.icon} />}
              active={dataViewUnit === 'percentage'}
              onClick={() => setDataViewUnit('percentage')}
              small
              title="Show percentage"
            />
          </B.ButtonGroup>
          <B.Text
            className={B.Classes.TEXT_MUTED}
            style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
          >
            {getSummaryText(
              dataViewUnit,
              organization && getAreaUnit(organization) === C.UNIT_AREA_ACRE ? 'acres' : 'hectares',
              currentCategoryId === 'multiCategory'
                ? 'multiCategory'
                : layer?.layerLegendMap?.[currentCategoryId]?.label || currentCategoryId
            )}
          </B.Text>
        </>
      }
    />
  );
};

const BreakdownToggleAndSelector: React.FC<{
  selectItems: {label: string; value: string}[];
  currentCategoryId: string;
  setCurrentCategoryId: React.Dispatch<React.SetStateAction<string>>;
  postSelectCategory: () => void;
  layer: ReturnType<typeof getLayer>;
}> = ({selectItems, currentCategoryId, setCurrentCategoryId, postSelectCategory, layer}) => {
  const handleItemSelect = (item: string, categoryId?: string) => {
    if (item === 'multiCategory') {
      setCurrentCategoryId('multiCategory');
      postSelectCategory();
    } else if (item === 'singleCategory' && categoryId) {
      setCurrentCategoryId(categoryId);
      postSelectCategory();
    }
  };

  const selectedCategoryLabel =
    selectItems.find((item) => item.value === currentCategoryId)?.label || '';

  return (
    <B.Popover
      minimal
      position={B.Position.BOTTOM_LEFT}
      content={
        <B.Menu>
          <B.MenuItem
            text="Category breakdown"
            active={currentCategoryId === 'multiCategory'}
            onClick={() => handleItemSelect('multiCategory')}
          />
          <B.MenuItem
            text="Property breakdown"
            active={currentCategoryId !== 'multiCategory'}
            popoverProps={{hoverCloseDelay: 300}}
          >
            {selectItems.map((item) => {
              // Get the color for this category if layer is an image layer with layerLegendMap
              const hasLegendMap = layer?.type === 'image' && layer.layerLegendMap;
              const categoryColor = hasLegendMap
                ? layer.layerLegendMap?.[item.value]?.color || '#cccccc'
                : '#cccccc';

              return (
                <B.MenuItem
                  key={item.value}
                  text={item.label}
                  active={currentCategoryId === item.value}
                  onClick={() => handleItemSelect('singleCategory', item.value)}
                  icon={
                    hasLegendMap && (
                      <div
                        style={{
                          width: '0.75rem',
                          height: '0.75rem',
                          borderRadius: '50%',
                          backgroundColor: categoryColor,
                        }}
                      />
                    )
                  }
                />
              );
            })}
          </B.MenuItem>
        </B.Menu>
      }
      className={tableCs.breakdownOptions}
    >
      <B.Button
        outlined
        rightIcon="caret-down"
        className={tableCs.categorySelectBordered}
        text={
          currentCategoryId === 'multiCategory'
            ? 'Category breakdown'
            : `Property breakdown (${selectedCategoryLabel})`
        }
      />
    </B.Popover>
  );
};
